(function(){
// Healthy Brain motion system: one lightweight canvas "field" (neural → botanical → nutrient particles)
// plus scroll/reveal helpers. No dependencies. Pauses off-screen; static frame under reduced motion.
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const ss = t => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;
const rng = seed => () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;

const L = {
  scatter: () => (i, n, w, h, s) => [s[0] * w, s[1] * h],
  net: (cx, cy, rx, ry) => (i, n, w, h, s) => {
    const a = s[0] * 6.283, r = 0.22 + 0.78 * Math.sqrt(s[1]);
    return [w * cx + Math.cos(a) * r * w * rx, h * cy + Math.sin(a) * r * h * ry];
  },
  bands: () => (i, n, w, h, s) => [(0.05 + s[0] * 0.9) * w, h * (0.2 + (i % 4) * 0.2) + (s[2] - 0.5) * h * 0.05],
  ring: (cx, cy, rr) => (i, n, w, h, s) => {
    const a = (i / n) * 6.283 + s[2] * 0.35, r = Math.min(w, h) * rr * (0.82 + s[1] * 0.36);
    return [w * cx + Math.cos(a) * r, h * cy + Math.sin(a) * r];
  }
};

function field(canvas, cfg) {
  const ctx = canvas.getContext('2d');
  const n = cfg.count, R = rng(cfg.seed || 11);
  const base = Array.from({ length: n }, (_, i) => ({ s: [R(), R(), R()], ph: R() * 6.283, sp: 0.25 + R() * 0.5, depth: 0.3 + R() * 0.9, leaf: i % 4 === 0, rot: R() * 6.283 }));
  const leafCol = cfg.leafCol || '104,146,114';
  let w = 0, h = 0, layouts = [], raf = 0, alive = true, vis = true, mx = 0, my = 0, tx = 0, ty = 0;
  function resize() {
    const r = canvas.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1);
    w = r.width; h = r.height;
    canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    layouts = cfg.phases.map(P => base.map((b, i) => P.layout(i, n, w, h, b.s)));
    if (cfg.reduced) draw(performance.now());
  }
  function draw(now) {
    if (!w) return;
    const t = now / 1000, p = clamp(cfg.reduced ? 1 : cfg.progress());
    const k = cfg.phases.length - 1, f = p * k, i0 = Math.min(k - 1, Math.floor(f)), lt = ss(clamp(f - i0));
    const A = cfg.phases[i0], B = cfg.phases[i0 + 1], mix = key => lerp(A[key], B[key], lt);
    const line = mix('line'), drawF = mix('draw'), leaf = mix('leaf'), dot = mix('dot'), drift = cfg.reduced ? 0 : mix('drift');
    const col = [0, 1, 2].map(j => Math.round(lerp(A.col[j], B.col[j], lt))).join(',');
    mx += (tx - mx) * 0.06; my += (ty - my) * 0.06;
    ctx.clearRect(0, 0, w, h);
    const ex = cfg.exclude ? cfg.exclude(w, h) : null;
    const pts = base.map((b, i) => {
      const a = layouts[i0][i], c = layouts[i0 + 1][i];
      const x = lerp(a[0], c[0], lt) + Math.sin(t * b.sp + b.ph) * drift + mx * b.depth * 16;
      const y = lerp(a[1], c[1], lt) + Math.cos(t * b.sp * 0.8 + b.ph) * drift + my * b.depth * 12;
      let fade = 1;
      if (ex) fade = clamp((Math.hypot(x - ex.x, y - ex.y) - ex.r) / (ex.r * 0.4));
      return [x, y, fade];
    });
    const md = cfg.maxDist || Math.min(w, h) * 0.24;
    if (line > 0.01) {
      ctx.lineWidth = 1;
      for (let i = 0; i < n; i++) {
        const a = pts[i];
        const fr = clamp(drawF * 1.7 - (i % 7) * 0.1);
        if (fr <= 0) continue;
        for (let j = i + 1; j < n; j++) {
          const b = pts[j], dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy);
          if (d > md) continue;
          const al = line * (1 - d / md) * Math.min(a[2], b[2]);
          if (al < 0.01) continue;
          ctx.strokeStyle = `rgba(${col},${al.toFixed(3)})`;
          ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(a[0] + dx * fr, a[1] + dy * fr); ctx.stroke();
        }
      }
    }
    for (let i = 0; i < n; i++) {
      const a = pts[i], b = base[i];
      if (a[2] < 0.02) continue;
      ctx.fillStyle = `rgba(${col},${(dot * a[2] * (0.35 + b.depth * 0.5)).toFixed(3)})`;
      ctx.beginPath(); ctx.arc(a[0], a[1], 1 + b.depth * 1.6, 0, 6.283); ctx.fill();
      if (b.leaf && leaf > 0.02) {
        const s = (7 + b.depth * 10) * leaf;
        ctx.save(); ctx.translate(a[0], a[1]); ctx.rotate(b.rot + Math.sin(t * 0.4 + b.ph) * 0.15);
        ctx.fillStyle = `rgba(${leafCol},${(0.5 * leaf * a[2]).toFixed(3)})`;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(s * 0.5, -s * 0.42, s, 0); ctx.quadraticCurveTo(s * 0.5, s * 0.42, 0, 0); ctx.fill();
        ctx.restore();
      }
    }
  }
  function loop(now) { if (!alive) return; raf = requestAnimationFrame(loop); if (vis && !document.hidden) draw(now); }
  const ro = new ResizeObserver(resize); ro.observe(canvas);
  const io = new IntersectionObserver(es => { vis = es[0].isIntersecting; }, { rootMargin: '120px' }); io.observe(canvas);
  const onMove = e => { tx = e.clientX / window.innerWidth - 0.5; ty = e.clientY / window.innerHeight - 0.5; };
  if (cfg.pointer && !cfg.reduced) window.addEventListener('pointermove', onMove, { passive: true });
  resize();
  if (!cfg.reduced) raf = requestAnimationFrame(loop);
  return { destroy() { alive = false; cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); window.removeEventListener('pointermove', onMove); } };
}

// Scroll progress of a tall section through the viewport: 0 when its top hits the top, 1 when its bottom hits the bottom.
function sectionProgress(el) {
  if (!el) return 0;
  const r = el.getBoundingClientRect(), span = r.height - window.innerHeight;
  return span > 0 ? clamp(-r.top / span) : clamp(1 - r.top / window.innerHeight);
}

// Elements with data-at (and optional data-until) fade/rise with a progress value.
function applyScenes(root, p) {
  if (!root) return;
  root.querySelectorAll('[data-at]').forEach(el => {
    const at = +el.dataset.at, until = el.dataset.until ? +el.dataset.until : 9;
    const fi = ss(clamp((p - at) / 0.05)), fo = ss(clamp((until - p) / 0.05)), v = Math.min(fi, fo);
    el.style.opacity = v.toFixed(3);
    el.style.transform = `translateY(${((1 - fi) * 28 - (1 - fo) * 22).toFixed(1)}px)`;
    el.style.pointerEvents = v > 0.5 ? '' : 'none';
  });
}
function clearScenes(root) {
  if (!root) return;
  root.querySelectorAll('[data-at]').forEach(el => { el.style.opacity = ''; el.style.transform = ''; el.style.pointerEvents = ''; });
}

// Cursor-follow neural trail (desktop, mouse only). allow(x,y) can suppress drawing (e.g. over checkout).
function trail(canvas, allow) {
  const ctx = canvas.getContext('2d'), R = rng(7);
  let pts = [], raf = 0, w = 0, h = 0, alive = true;
  const size = () => { const dpr = Math.min(2, window.devicePixelRatio || 1); w = window.innerWidth; h = window.innerHeight; canvas.width = w * dpr; canvas.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
  const onMove = e => {
    if (e.pointerType !== 'mouse' || !allow(e.clientX, e.clientY)) return;
    const last = pts[pts.length - 1];
    if (last && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 14) return;
    pts.push({ x: e.clientX, y: e.clientY, t: performance.now(), bx: (R() - 0.5) * 50, by: (R() - 0.5) * 50, b: R() < 0.45 });
    if (pts.length > 40) pts.shift();
    if (!raf) raf = requestAnimationFrame(draw);
  };
  function draw(now) {
    raf = 0; if (!alive) return;
    ctx.clearRect(0, 0, w, h);
    pts = pts.filter(p => now - p.t < 900);
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i], a = 1 - (now - p.t) / 900;
      if (i > 0) { const q = pts[i - 1]; ctx.strokeStyle = 'rgba(107,75,166,' + (a * 0.4).toFixed(3) + ')'; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.moveTo(q.x, q.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
      if (p.b) {
        const g = Math.min(1, (now - p.t) / 260), bx = p.x + p.bx * g, by = p.y + p.by * g;
        ctx.strokeStyle = 'rgba(107,75,166,' + (a * 0.28).toFixed(3) + ')'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(bx, by); ctx.stroke();
        ctx.fillStyle = 'rgba(104,146,114,' + (a * 0.5).toFixed(3) + ')'; ctx.beginPath(); ctx.arc(bx, by, 1.8, 0, 6.283); ctx.fill();
      }
      ctx.fillStyle = 'rgba(69,36,127,' + (a * 0.5).toFixed(3) + ')'; ctx.beginPath(); ctx.arc(p.x, p.y, 1.6 + a, 0, 6.283); ctx.fill();
    }
    if (pts.length) raf = requestAnimationFrame(draw);
  }
  size();
  window.addEventListener('resize', size);
  window.addEventListener('pointermove', onMove, { passive: true });
  return { destroy() { alive = false; cancelAnimationFrame(raf); window.removeEventListener('resize', size); window.removeEventListener('pointermove', onMove); ctx.clearRect(0, 0, w, h); } };
}

// Count-up for [data-count] numbers when they enter the viewport. Text is final value until JS runs.
function countUp(root, reduced) {
  if (reduced) return () => {};
  const els = [...root.querySelectorAll('[data-count]')].filter(el => el.getBoundingClientRect().top > window.innerHeight * 0.9);
  els.forEach(el => { el.textContent = '0'; });
  const io = new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) return;
    const el = e.target; io.unobserve(el);
    const to = +el.dataset.count, t0 = performance.now();
    const step = now => { const k = Math.min(1, (now - t0) / 1400); el.textContent = String(Math.round(to * (1 - Math.pow(1 - k, 3)))); if (k < 1) requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }), { threshold: 0.5 });
  els.forEach(el => io.observe(el));
  return () => io.disconnect();
}

// One-shot reveals for [data-reveal] (stagger via data-delay ms). Skips anything already on screen at load.
function reveals(root, sel = '[data-reveal]') {
  const els = [...root.querySelectorAll(sel)].filter(el => el.getBoundingClientRect().top > window.innerHeight * 0.9);
  els.forEach(el => { el.style.opacity = '0'; });
  const pending = new Set(els);
  const show = el => { if (!pending.has(el)) return; pending.delete(el); io.unobserve(el); el.style.opacity = ''; if (el.animate && !document.hidden) el.animate([{ opacity: 0, transform: 'translateY(26px)' }, { opacity: 1, transform: 'none' }], { duration: 760, delay: +(el.dataset.delay || 0), easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' }); };
  const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) show(e.target); }), { threshold: 0.15 });
  els.forEach(el => io.observe(el));
  let t = 0;
  const check = () => { t = 0; pending.forEach(el => { const r = el.getBoundingClientRect(); if (r.top < window.innerHeight * 0.95 && r.bottom > 0) show(el); }); };
  const onScroll = () => { if (!t) t = setTimeout(check, 200); };
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => { io.disconnect(); clearTimeout(t); window.removeEventListener('scroll', onScroll); };
}

window.HBMotion = { clamp, L, field, sectionProgress, applyScenes, clearScenes, trail, countUp, reveals };
})();
